import { StyledButtonGroup } from '@app/components/button-group/ButtonGroup';
import { StyledButton } from '@app/components/button/Button';
import { SelectField } from '@app/components/form-fields/SelectField';
import { TextField } from '@app/components/form-fields/TextField';
import type { ChannelPartner } from '@app/services/api.types';
import { countryOptions } from '@app/utils/constants';
import { zodResolver } from '@hookform/resolvers/zod';
import { useEffect } from 'react';
import type { UseFormReset } from 'react-hook-form';
import { Controller, useForm } from 'react-hook-form';
import { MdCheck } from 'react-icons/md';
import * as z from 'zod';

const schema = z.object({
    address_line1: z.string().trim().min(1, 'Address line 1 is required'),
    address_line2: z.string().trim(),
    city: z.string().trim().min(1, 'City is required'),
    contact_email: z
        .email('Admin email must be valid format')
        .trim()
        .min(1, 'Admin email is required'),
    contact_name: z.string().trim().min(1, 'Admin name is required'),
    contact_telephone: z.string().trim().min(1, 'Admin phone is required'),
    country: z.string().trim().min(1, 'Country is required'),
    name: z.string().trim().min(1, 'Company name is required'),
    post_code: z.string().trim().min(1, 'Post code is required'),
});

export type PartnerFormValues = z.infer<typeof schema>;

export type PartnerFormProps = {
    id: string;
    data?: ChannelPartner;
    isEdit?: boolean;
    loading?: boolean;
    onCancel?: (e: React.MouseEvent<HTMLButtonElement>) => void;
    onSubmit: (data: PartnerFormValues, reset: UseFormReset<PartnerFormValues>) => void;
};

export const PartnerForm = ({ onSubmit, onCancel, isEdit, data, ...props }: PartnerFormProps) => {
    const {
        handleSubmit,
        control,
        reset,
        formState: { isValid, isSubmitting },
    } = useForm<PartnerFormValues>({
        mode: 'all',
        resolver: zodResolver(schema),
        defaultValues: {
            name: '',
            contact_name: '',
            contact_email: '',
            contact_telephone: '',
            address_line1: '',
            address_line2: '',
            city: '',
            post_code: '',
            country: 'GB',
        },
    });

    useEffect(() => {
        reset({
            name: data?.name || '',
            contact_name: data?.contact_name || '',
            contact_email: data?.contact_email || '',
            contact_telephone: data?.contact_telephone || '',
            address_line1: data?.address_line1 || '',
            address_line2: data?.address_line2 || '',
            city: data?.city || '',
            post_code: data?.post_code || '',
            country: data?.country || 'GB',
        });
    }, [data, reset]);

    return (
        <form onSubmit={handleSubmit((values) => onSubmit(values, reset))} id={props.id}>
            <div className="form-fields-wrapper">
                <div className="grid">
                    <div className="grid-xs-12">
                        <Controller
                            control={control}
                            name="name"
                            render={({ field, fieldState }) => (
                                <TextField
                                    fieldState={fieldState}
                                    {...field}
                                    label="Channel Partner Name"
                                    type="text"
                                    placeholder="Enter channel partner name"
                                />
                            )}
                        />
                    </div>

                    <div className="grid-xs-12 grid-s-6 grid-md-6">
                        <Controller
                            control={control}
                            name="contact_name"
                            render={({ field, fieldState }) => (
                                <TextField
                                    fieldState={fieldState}
                                    {...field}
                                    label="Admin Name"
                                    type="text"
                                    placeholder="Enter admin name"
                                />
                            )}
                        />
                    </div>

                    <div className="grid-xs-12 grid-s-6 grid-md-6">
                        <Controller
                            control={control}
                            name="contact_email"
                            render={({ field, fieldState }) => (
                                <TextField
                                    fieldState={fieldState}
                                    {...field}
                                    label="Admin Email"
                                    type="email"
                                    placeholder="Enter admin email"
                                />
                            )}
                        />
                    </div>

                    <div className="grid-xs-12 grid-s-12 grid-md-12">
                        <Controller
                            control={control}
                            name="contact_telephone"
                            render={({ field, fieldState }) => (
                                <TextField
                                    fieldState={fieldState}
                                    {...field}
                                    disabled={isEdit}
                                    label="Admin Telephone"
                                    type="text"
                                    placeholder="Enter admin telephone"
                                />
                            )}
                        />
                    </div>

                    <div className="grid-xs-12 grid-s-12 grid-md-12">
                        <Controller
                            control={control}
                            name="address_line1"
                            render={({ field, fieldState }) => (
                                <TextField
                                    fieldState={fieldState}
                                    {...field}
                                    label="Address Line 1"
                                    type="text"
                                    placeholder="Enter address line 1"
                                />
                            )}
                        />
                    </div>

                    <div className="grid-xs-12 grid-s-12 grid-md-12">
                        <Controller
                            control={control}
                            name="address_line2"
                            render={({ field, fieldState }) => (
                                <TextField
                                    fieldState={fieldState}
                                    {...field}
                                    label="Address Line 2"
                                    type="text"
                                    placeholder="Enter address line 2 (optional)"
                                />
                            )}
                        />
                    </div>

                    <div className="grid-xs-12 grid-s-12 grid-md-6">
                        <Controller
                            control={control}
                            name="city"
                            render={({ field, fieldState }) => (
                                <TextField
                                    fieldState={fieldState}
                                    {...field}
                                    label="City"
                                    type="text"
                                    placeholder="Enter city"
                                />
                            )}
                        />
                    </div>

                    <div className="grid-xs-12 grid-s-12 grid-md-6">
                        <Controller
                            control={control}
                            name="post_code"
                            render={({ field, fieldState }) => (
                                <TextField
                                    fieldState={fieldState}
                                    {...field}
                                    label="Post Code"
                                    type="text"
                                    placeholder="Enter post code"
                                />
                            )}
                        />
                    </div>

                    <div className="grid-xs-12 grid-s-12 grid-md-12">
                        <Controller
                            control={control}
                            name="country"
                            render={({ field, fieldState }) => (
                                <SelectField
                                    fieldState={fieldState}
                                    {...field}
                                    label="Country"
                                    placeholder="Select Country"
                                    options={countryOptions}
                                />
                            )}
                        />
                    </div>
                </div>
            </div>

            <div className="ant-modal-footer">
                <StyledButtonGroup justify="flex-end">
                    <StyledButton onClick={onCancel} color="black" data-testid="reset-button">
                        Cancel
                    </StyledButton>
                    <StyledButton
                        htmlType="submit"
                        type="primary"
                        disabled={!isValid}
                        loading={isSubmitting || props?.loading}
                        icon={<MdCheck />}
                        data-testid="register-Partner-button">
                        Save
                    </StyledButton>
                </StyledButtonGroup>
            </div>
        </form>
    );
};
